"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface FeaturedProjectGalleryItem {
  src: string;
  alt?: string;
}

export interface FeaturedProjectItem {
  id: string;
  title: string;
  /** Dòng phụ trên card (hover), vd. loại hình / địa điểm */
  subtitle: string;
  description: string;
  gallery: FeaturedProjectGalleryItem[];
}

/** @deprecated Categories removed — use flat `projects` instead. */
export interface FeaturedProjectsDathaconsCategory {
  id: string;
  label: string;
  projects: FeaturedProjectItem[];
}

export interface FeaturedProjectsDathaconsProps {
  sectionId?: string;
  heading?: string;
  projects?: FeaturedProjectItem[];
  /** @deprecated Use `projects` — all categories are flattened when present. */
  categories?: FeaturedProjectsDathaconsCategory[];
  defaultCategoryId?: string;
  /** Desktop columns (default 3). */
  projectsPerPage?: number;
  /** Rows per page — desktop shows columns × rows (default 3×3 = 9). */
  rowsPerPage?: number;
}

const ORANGE = "#ffa411";
const COLS_DESKTOP = 3;
const ROWS_PER_PAGE_DEFAULT = 3;
const PER_PAGE_MOBILE_ROWS = 3;

function resolvePerPage(
  isDesktop: boolean,
  projectsPerPage: number | undefined,
  rowsPerPage: number,
) {
  if (projectsPerPage != null && projectsPerPage > 0) return projectsPerPage;
  return isDesktop ? COLS_DESKTOP * rowsPerPage : PER_PAGE_MOBILE_ROWS;
}

function SlideArrow({
  direction,
  onClick,
  disabled,
}: {
  direction: "prev" | "next";
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e0e0e0] bg-white text-[#303030] shadow-sm transition hover:border-[#ffa411] hover:text-[#ffa411] disabled:cursor-default disabled:opacity-35 md:h-11 md:w-11"
      aria-label={direction === "prev" ? "Dự án trước" : "Dự án tiếp theo"}
    >
      <SlideArrowIcon direction={direction} />
    </button>
  );
}

function SlideArrowIcon({ direction }: { direction: "prev" | "next" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5 md:h-6 md:w-6"
      aria-hidden
    >
      {direction === "prev" ? <path d="M15 6l-6 6 6 6" /> : <path d="M9 6l6 6-6 6" />}
    </svg>
  );
}

function SlidePagination({
  totalPages,
  pageIndex,
  onSelect,
  onPrev,
  onNext,
  canPrev,
  canNext,
}: {
  totalPages: number;
  pageIndex: number;
  onSelect: (index: number) => void;
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
}) {
  if (totalPages <= 1) return null;

  return (
    <nav
      className="flex w-full items-center justify-center gap-4 pt-8 md:gap-6 md:pt-10"
      aria-label="Phân trang dự án"
    >
      <SlideArrow direction="prev" onClick={onPrev} disabled={!canPrev} />
      <div className="flex items-center gap-2.5">
        {Array.from({ length: totalPages }, (_, i) => {
          const active = i === pageIndex;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onSelect(i)}
              className={`rounded-full transition-all duration-200 ${
                active
                  ? "h-3 w-3 bg-[#ffa411] ring-2 ring-[#ffa411]/30"
                  : "h-2.5 w-2.5 bg-[#d4d4d4] hover:bg-[#ffa411]/60"
              }`}
              aria-label={`Trang ${i + 1}`}
              aria-current={active ? "true" : undefined}
            />
          );
        })}
      </div>
      <SlideArrow direction="next" onClick={onNext} disabled={!canNext} />
    </nav>
  );
}

function resolveProjects({
  projects,
  categories,
}: Pick<FeaturedProjectsDathaconsProps, "projects" | "categories">) {
  if (projects?.length) return projects;
  if (categories?.length) return categories.flatMap((c) => c.projects);
  return [];
}

export default function FeaturedProjectsDathacons({
  sectionId = "projects",
  heading = "Dự án tiêu biểu",
  projects: projectsProp,
  categories,
  projectsPerPage,
  rowsPerPage = ROWS_PER_PAGE_DEFAULT,
}: FeaturedProjectsDathaconsProps) {
  const projects = useMemo(
    () => resolveProjects({ projects: projectsProp, categories }),
    [projectsProp, categories],
  );

  const [pageIndex, setPageIndex] = useState(0);
  const [perPage, setPerPage] = useState(() => projectsPerPage ?? COLS_DESKTOP * rowsPerPage);
  const [modalProject, setModalProject] = useState<FeaturedProjectItem | null>(null);
  const [modalPhotoIndex, setModalPhotoIndex] = useState(0);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setPerPage(resolvePerPage(mq.matches, projectsPerPage, rowsPerPage));
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [projectsPerPage, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(projects.length / perPage));

  useEffect(() => {
    setPageIndex((i) => Math.min(i, totalPages - 1));
  }, [totalPages]);

  const visibleProjects = useMemo(() => {
    const start = pageIndex * perPage;
    return projects.slice(start, start + perPage);
  }, [projects, pageIndex, perPage]);

  const canPrev = pageIndex > 0;
  const canNext = pageIndex < totalPages - 1;

  const goPrev = useCallback(() => {
    setPageIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setPageIndex((i) => Math.min(totalPages - 1, i + 1));
  }, [totalPages]);

  const goToPage = useCallback(
    (index: number) => {
      setPageIndex(Math.max(0, Math.min(totalPages - 1, index)));
    },
    [totalPages],
  );

  const openModal = useCallback((p: FeaturedProjectItem, startIndex = 0) => {
    scrollPositionRef.current = window.scrollY;
    setModalProject(p);
    setModalPhotoIndex(Math.min(startIndex, Math.max(0, p.gallery.length - 1)));
  }, []);

  const closeModal = useCallback(() => {
    setModalProject(null);
  }, []);

  useEffect(() => {
    if (!modalProject) return;
    const html = document.documentElement;
    const body = document.body;
    const scrollY = scrollPositionRef.current;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowLeft" && modalProject.gallery.length > 1) {
        setModalPhotoIndex((i) => (i <= 0 ? modalProject.gallery.length - 1 : i - 1));
      }
      if (e.key === "ArrowRight" && modalProject.gallery.length > 1) {
        setModalPhotoIndex((i) => (i >= modalProject.gallery.length - 1 ? 0 : i + 1));
      }
    };
    window.addEventListener("keydown", onKey);

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    return () => {
      window.removeEventListener("keydown", onKey);
      const y = scrollPositionRef.current;
      html.style.overflow = "";
      body.style.overflow = "";
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ left: 0, top: y, behavior: "auto" });
        });
      });
    };
  }, [modalProject, closeModal]);

  const modalMain = modalProject?.gallery[modalPhotoIndex];

  if (projects.length === 0) return null;

  return (
    <section
      id={sectionId}
      className="relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 bg-white py-10 md:py-14"
      data-name="Featured projects section"
    >
      <div className="flex w-full flex-col items-center gap-8 md:gap-10">
        <header className="flex w-full flex-col items-center gap-3">
          <h2
            className="text-center text-[29px] font-extrabold leading-tight tracking-tight md:text-[37px]"
            style={{ color: ORANGE }}
          >
            {heading}
          </h2>
          <div className="h-px w-14 bg-[#c8c8c8]" aria-hidden />
        </header>

        <div className="relative w-full">
          <div className="grid w-full grid-cols-1 gap-px bg-white md:grid-cols-3" key={pageIndex}>
            {visibleProjects.map((proj) => {
              const cover = proj.gallery[0];
              if (!cover) return null;
              return (
                <button
                  key={proj.id}
                  type="button"
                  onClick={() => openModal(proj, 0)}
                  className="group relative aspect-[3/4] w-full cursor-pointer overflow-hidden bg-[#ececec] text-left outline-none transition-opacity duration-300 hover:opacity-95 focus-visible:ring-2 focus-visible:ring-[#ffa411] focus-visible:ring-offset-2"
                >
                  <img
                    src={cover.src}
                    alt={cover.alt ?? proj.title}
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] group-focus-visible:scale-[1.03]"
                    loading="lazy"
                    decoding="async"
                  />
                  <div
                    className="absolute inset-x-0 bottom-0 z-[1] bg-gradient-to-t from-black/55 via-black/20 to-transparent px-4 pb-5 pt-16 md:px-5 md:pb-6"
                    aria-hidden
                  />
                  <p className="absolute inset-x-0 bottom-0 z-[2] px-4 pb-5 text-center text-[13px] font-medium uppercase leading-snug tracking-wide text-white md:px-5 md:pb-6 md:text-[15px]">
                    {proj.title}
                  </p>
                </button>
              );
            })}
          </div>

          <SlidePagination
            totalPages={totalPages}
            pageIndex={pageIndex}
            onSelect={goToPage}
            onPrev={goPrev}
            onNext={goNext}
            canPrev={canPrev}
            canNext={canNext}
          />
        </div>
      </div>

      {modalProject &&
        modalMain &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center overscroll-none p-3 pt-[max(12px,env(safe-area-inset-top))] pb-[max(12px,env(safe-area-inset-bottom))] md:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="featured-project-modal-title"
          >
            <button
              type="button"
              className="absolute inset-0 bg-[#0a0a0a]/75 backdrop-blur-[10px] transition-opacity"
              aria-label="Đóng"
              onClick={closeModal}
            />
            <div className="relative z-[1] flex max-h-[min(90dvh,900px)] w-full max-w-[1100px] min-h-0 flex-col overflow-hidden rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.45)] ring-1 ring-white/15 md:max-h-[min(92vh,900px)] md:flex-row">
              <div className="relative flex min-h-[200px] flex-1 flex-col bg-[#0f0f0f] md:min-h-0">
                <div className="relative flex min-h-[240px] flex-1 items-center justify-center p-4 md:min-h-[min(62vh,520px)] md:p-6">
                  <img
                    src={modalMain.src}
                    alt={modalMain.alt ?? modalProject.title}
                    className="max-h-[min(58vh,500px)] w-full object-contain md:max-h-[min(62vh,540px)]"
                  />
                </div>
                {modalProject.gallery.length > 1 && (
                  <div className="border-t border-white/10 bg-black/40 px-3 py-3 md:px-4">
                    <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
                      {modalProject.gallery.map((g, idx) => (
                        <button
                          key={`${g.src}-${idx}`}
                          type="button"
                          onClick={() => setModalPhotoIndex(idx)}
                          className={`relative h-16 w-[88px] shrink-0 overflow-hidden rounded-lg border-2 transition-all duration-200 md:h-[72px] md:w-[100px] ${
                            idx === modalPhotoIndex
                              ? "border-[#ffa411] ring-2 ring-[#ffa411]/40"
                              : "border-transparent opacity-75 hover:opacity-100"
                          }`}
                        >
                          <img src={g.src} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <aside className="flex w-full flex-col justify-between border-t border-black/5 bg-gradient-to-b from-[#fafafa] to-white md:w-[min(380px,38%)] md:border-l md:border-t-0">
                <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5 md:p-7">
                  <div>
                    <p className="mb-1 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#ffa411]">
                      Dự án
                    </p>
                    <h3
                      id="featured-project-modal-title"
                      className="text-[23px] font-extrabold leading-snug text-[#1a1a1a] md:text-[25px]"
                    >
                      {modalProject.title}
                    </h3>
                  </div>
                  <p className="text-[15px] leading-relaxed text-[#4a4a4a] md:text-[16px]">
                    {modalProject.description}
                  </p>
                </div>
                <div className="flex items-center justify-end gap-2 border-t border-black/[0.06] p-4 md:p-5">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="rounded-lg bg-[#1a1a1a] px-5 py-2.5 text-[14px] font-semibold text-white transition hover:bg-[#333]"
                  >
                    Đóng
                  </button>
                </div>
              </aside>

              <button
                type="button"
                onClick={closeModal}
                className="absolute right-3 top-3 z-[2] flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[21px] font-light text-[#1a1a1a] shadow-md transition hover:bg-white md:right-4 md:top-4"
                aria-label="Đóng hộp thoại"
              >
                ×
              </button>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
}

FeaturedProjectsDathacons.displayName = "FeaturedProjectsDathacons";
